import { useMemo } from 'react';

import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { CampaignSubscriberWithJourney } from '@/shared/types/campaigns';

interface SubscriberJourneyHeaderProps {
  subscriber: CampaignSubscriberWithJourney;
  currentIndex?: number;
  totalCount?: number;
  onNavigate?: (direction: 'prev' | 'next') => void;
}

export const SubscriberJourneyHeader = ({
  subscriber,
  currentIndex,
  totalCount,
  onNavigate,
}: SubscriberJourneyHeaderProps) => {
  const { t } = useTranslation();

  const displayName = useMemo(() => {
    const user = (subscriber.context as Record<string, unknown>)?.user as
      Record<string, unknown> | undefined;
    const name = user?.display_name as string | undefined;
    return (
      name || subscriber.phone || subscriber.email || t('campaigns.subscriber_journey.unknown')
    );
  }, [subscriber, t]);

  const showNavigation = currentIndex !== undefined && totalCount !== undefined && onNavigate;

  return (
    <div className="flex items-center justify-between gap-4">
      <h1 className="text-2xl font-bold">{displayName}</h1>

      {showNavigation && (
        <div className="flex items-center gap-2">
          <span className="text-sm leading-6 font-normal text-muted-foreground tabular-nums">
            <span className="text-foreground">{currentIndex + 1}</span>/{totalCount}
          </span>
          <Button
            variant="ghost-neutral"
            size="icon-sm"
            className="h-auto w-auto p-0.5"
            onClick={() => onNavigate('prev')}
            disabled={currentIndex <= 0}
          >
            <ChevronUpIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost-neutral"
            size="icon-sm"
            className="h-auto w-auto p-0.5"
            onClick={() => onNavigate('next')}
            disabled={currentIndex >= totalCount - 1}
          >
            <ChevronDownIcon className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
